/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { useApi } from 'shared_components/context';
import {
  LookupLookupReleaseResponseSourceEnum,
  CreateReleaseAndLinkRequestSourceEnum,
} from '../../../../../shared_components/generated/admin';
import type { Row } from '../MasterDiscographyTypes';
import { friendlySaveError } from './saveErrorMessage';

/**
 * Change/Link Release modal — extracted from RecordingEditForm so it can be
 * reused by the bulk-edit builder.
 *
 * - mode="apply" (recording edit form): performs the create-and-link /
 *   link-earliest API call for a single recording, then hands the updated Row
 *   to `onApplied`.
 * - mode="select" (bulk edit): does the (read-only) lookup so the user can pick
 *   a release, but does NOT link — it just returns the chosen release via
 *   `onApplied`, leaving the actual per-recording linking to the bulk apply.
 */

const MIN_RELEASE_YEAR = 1920;
const MAX_RELEASE_YEAR_OFFSET = 2; // current year + 2

const SELECT_BASE_CLASS =
  'wmc-select tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-pl-3 tw-py-2 tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800';
const selectTextClass = (hasValue: boolean): string =>
  hasValue ? 'tw-text-white' : 'tw-text-gray-400';

type ReleaseSourceForm =
  | 'Discogs'
  | 'Deezer'
  | 'Spotify'
  | 'MusicBrainz'
  | 'PPL'
  | 'Other';

/** Validate a 4-digit release year. Returns an error message or null. */
export function validateReleaseYear(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null; // optional field
  if (!/^\d{4}$/.test(trimmed)) return 'Year must be a 4-digit number';
  const year = Number(trimmed);
  const maxYear = new Date().getFullYear() + MAX_RELEASE_YEAR_OFFSET;
  if (year < MIN_RELEASE_YEAR)
    return `Year cannot be before ${MIN_RELEASE_YEAR}`;
  if (year > maxYear) return `Year cannot be after ${maxYear}`;
  return null;
}

function findDuplicate(values: string[]): string | null {
  const seen = new Set<string>();
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) return v;
    seen.add(key);
  }
  return null;
}

function addMultipleValue(
  setter: React.Dispatch<React.SetStateAction<string[]>>,
  value: string
) {
  const trimmed = value.trim();
  if (trimmed) {
    setter((prev) =>
      prev.some((v) => v.toLowerCase() === trimmed.toLowerCase())
        ? prev
        : [...prev, trimmed]
    );
  }
}

function mapReleaseSourceToLookupEnum(
  source: ReleaseSourceForm
):
  | (typeof LookupLookupReleaseResponseSourceEnum)[keyof typeof LookupLookupReleaseResponseSourceEnum]
  | null {
  switch (source) {
    case 'Discogs':
      return LookupLookupReleaseResponseSourceEnum.Discogs;
    case 'Deezer':
      return LookupLookupReleaseResponseSourceEnum.Deezer;
    case 'Spotify':
      return LookupLookupReleaseResponseSourceEnum.Spotify;
    case 'MusicBrainz':
      return LookupLookupReleaseResponseSourceEnum.Mb;
    case 'PPL':
      return LookupLookupReleaseResponseSourceEnum.Ppl;
    default:
      return null;
  }
}

function mapReleaseSourceToCreateEnum(
  source: ReleaseSourceForm
): CreateReleaseAndLinkRequestSourceEnum {
  switch (source) {
    case 'Discogs':
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
    case 'Deezer':
      return CreateReleaseAndLinkRequestSourceEnum.Deezer;
    case 'Spotify':
      return CreateReleaseAndLinkRequestSourceEnum.Spotify;
    case 'MusicBrainz':
      return CreateReleaseAndLinkRequestSourceEnum.Mb;
    case 'PPL':
      return CreateReleaseAndLinkRequestSourceEnum.Ppl;
    case 'Other':
      return CreateReleaseAndLinkRequestSourceEnum.Other;
    default:
      return CreateReleaseAndLinkRequestSourceEnum.Discogs;
  }
}

const MultiValueAddField: React.FC<{
  label: string;
  addLabel: string;
  placeholder: string;
  values: string[];
  onRemove: (index: number) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  commit: () => void;
  inputId?: string;
}> = ({
  label,
  addLabel,
  placeholder,
  values,
  onRemove,
  open,
  setOpen,
  value,
  setValue,
  commit,
  inputId,
}) => (
  <div>
    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
      <label
        htmlFor={inputId}
        className="tw-block tw-text-white tw-text-sm tw-font-medium"
      >
        {label}
      </label>
      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">Optional</span>
    </div>
    {values.length > 0 && (
      <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mb-2">
        {values.map((v, index) => (
          <div
            key={index}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-xs tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-700 tw-text-white"
          >
            <span>{v}</span>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="tw-ml-1 tw-text-gray-400 hover:tw-text-white"
              title="Remove"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    )}
    {values.length === 0 && (
      <p className="tw-text-gray-400 tw-text-xs tw-mb-2">None</p>
    )}
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      {open ? (
        <>
          <input
            id={inputId}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && value.trim()) {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                setValue('');
                setOpen(false);
              }
            }}
            className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue"
          />
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => {
                setValue('');
                setOpen(false);
              }}
            />
            <WButton
              variant="primary"
              label="Create"
              disabled={!value.trim()}
              onClick={commit}
            />
          </div>
        </>
      ) : (
        <div className="tw-flex tw-justify-end">
          <button
            type="button"
            className="tw-text-sm tw-flex tw-items-center tw-text-textWhite hover:tw-text-textLime"
            onClick={() => {
              setValue('');
              setOpen(true);
            }}
          >
            <i className="wi wi-20px wi-plus tw-mr-1" />
            <span className="tw-underline">{addLabel}</span>
          </button>
        </div>
      )}
    </div>
  </div>
);

export interface ChangeReleaseCurrent {
  release?: string | null;
  year?: string | null;
  format?: string | null;
  mediaType?: string | null;
}

interface ChangeReleaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'apply' | 'select';
  userId?: string;
  /** The recording context used for lookup / merge. In bulk 'select' mode this
   *  can be a minimal { id } stand-in (the first selected recording). */
  row: Row | null;
  current?: ChangeReleaseCurrent;
  currentLabels?: string[];
  currentCatNumbers?: string[];
  availableFormats?: string[];
  availableTypes?: string[];
  onApplied: (updatedRow: Row) => void;
}

export const ChangeReleaseModal: React.FC<ChangeReleaseModalProps> = ({
  isOpen,
  onClose,
  mode = 'apply',
  userId,
  row,
  current = {},
  currentLabels = [],
  currentCatNumbers = [],
  availableFormats = [],
  availableTypes = [],
  onApplied,
}) => {
  const { adminApi: api } = useApi();

  const [releaseId, setReleaseId] = useState('');
  const [releaseSource, setReleaseSource] =
    useState<ReleaseSourceForm>('Discogs');
  const [sourceUrl, setSourceUrl] = useState('');
  const [newReleaseTitle, setNewReleaseTitle] = useState('');
  const [newReleaseYear, setNewReleaseYear] = useState('');
  const [newReleaseLabels, setNewReleaseLabels] = useState<string[]>([]);
  const [newReleaseCatNumbers, setNewReleaseCatNumbers] = useState<string[]>(
    []
  );
  const [newLabelInputOpen, setNewLabelInputOpen] = useState(false);
  const [newLabelInputValue, setNewLabelInputValue] = useState('');
  const [newCatNoInputOpen, setNewCatNoInputOpen] = useState(false);
  const [newCatNoInputValue, setNewCatNoInputValue] = useState('');
  const [newReleaseFormat, setNewReleaseFormat] = useState('');
  const [newReleaseType, setNewReleaseType] = useState('');
  const [releaseLookupLoading, setReleaseLookupLoading] = useState(false);
  const [releaseLookupError, setReleaseLookupError] = useState<string | null>(
    null
  );
  const [releaseLookupSuccess, setReleaseLookupSuccess] = useState(false);
  const [releaseLookupNotFound, setReleaseLookupNotFound] = useState(false);
  const [releaseLookupNotLinked, setReleaseLookupNotLinked] = useState(false);
  const [releaseNotLinkedConfirmed, setReleaseNotLinkedConfirmed] =
    useState(false);
  const [lookupReleaseId, setLookupReleaseId] = useState('');
  const [lookupEdgeId, setLookupEdgeId] = useState<string | null>(null);
  const [lookupDateReleased, setLookupDateReleased] = useState<string | null>(
    null
  );
  const [releaseChangeSubmitError, setReleaseChangeSubmitError] = useState<
    string | null
  >(null);
  const [releaseChangeSubmitLoading, setReleaseChangeSubmitLoading] =
    useState(false);
  const [releaseSameAsCurrentError, setReleaseSameAsCurrentError] =
    useState(false);
  const [newReleaseYearError, setNewReleaseYearError] = useState<string | null>(
    null
  );

  const commitNewLabel = () => {
    const trimmed = newLabelInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setNewReleaseLabels, trimmed);
    setNewLabelInputValue('');
    setNewLabelInputOpen(false);
  };
  const commitNewCatNo = () => {
    const trimmed = newCatNoInputValue.trim();
    if (!trimmed) return;
    addMultipleValue(setNewReleaseCatNumbers, trimmed);
    setNewCatNoInputValue('');
    setNewCatNoInputOpen(false);
  };

  const handleReleaseLookup = async () => {
    if (!userId || !row?.id || !releaseId.trim()) {
      setReleaseLookupError(
        'User and recording context required. Enter a release ID.'
      );
      return;
    }
    if (releaseSource === 'Other') {
      setReleaseLookupError(
        'Use a catalog source (e.g. Discogs) to look up by ID.'
      );
      return;
    }
    const lookupSource = mapReleaseSourceToLookupEnum(releaseSource);
    if (!lookupSource) {
      setReleaseLookupError('Invalid source for lookup.');
      return;
    }
    const enteredId = releaseId.trim();
    const isSameRelease = (row.sourceReleaseId ?? []).some(
      (srcId) => srcId === enteredId || srcId.endsWith(`:${enteredId}`)
    );
    if (isSameRelease) {
      setReleaseSameAsCurrentError(true);
      return;
    }

    setReleaseSameAsCurrentError(false);
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setReleaseLookupLoading(true);
    try {
      const response = await api.lookupLookupReleaseResponse({
        userId,
        parentRecordingId: row.id,
        sourceId: releaseId.trim(),
        source: lookupSource,
      });
      const p = response.payload;
      setLookupReleaseId(p.releaseId);
      setLookupEdgeId(p.edgeId ?? null);
      setLookupDateReleased(p.dateReleased ?? null);
      if (p.releaseTitle) setNewReleaseTitle(p.releaseTitle);
      if (p.dateReleased) {
        const year =
          p.dateReleased.length >= 4
            ? p.dateReleased.slice(0, 4)
            : p.dateReleased;
        setNewReleaseYear(year);
        setNewReleaseYearError(validateReleaseYear(year));
      }
      if (p.releaseLabels?.length) setNewReleaseLabels([...p.releaseLabels]);
      if (p.catNumbers?.length) setNewReleaseCatNumbers([...p.catNumbers]);
      if (p.format) setNewReleaseFormat(p.format);
      if (p.mediaType) setNewReleaseType(p.mediaType);
      setReleaseLookupSuccess(true);
      if (!response.linked) {
        setReleaseLookupNotLinked(true);
      }
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      if (status === 404) {
        setReleaseLookupNotFound(true);
      } else {
        const message =
          err instanceof Error ? err.message : 'Release lookup failed';
        setReleaseLookupError(message);
      }
    } finally {
      setReleaseLookupLoading(false);
    }
  };

  const resetReleaseModalState = () => {
    setReleaseId('');
    setSourceUrl('');
    setReleaseSource('Discogs');
    setNewReleaseTitle('');
    setNewReleaseYear('');
    setNewReleaseLabels([]);
    setNewReleaseCatNumbers([]);
    setNewLabelInputOpen(false);
    setNewLabelInputValue('');
    setNewCatNoInputOpen(false);
    setNewCatNoInputValue('');
    setNewReleaseFormat('');
    setNewReleaseType('');
    setReleaseLookupError(null);
    setReleaseLookupSuccess(false);
    setReleaseLookupNotFound(false);
    setReleaseLookupNotLinked(false);
    setReleaseNotLinkedConfirmed(false);
    setReleaseSameAsCurrentError(false);
    setLookupReleaseId('');
    setLookupEdgeId(null);
    setLookupDateReleased(null);
    setReleaseChangeSubmitError(null);
    setNewReleaseYearError(null);
  };

  const mergeReleaseIntoRow = (): Row => {
    const base = (row ?? {}) as Row;
    return {
      ...base,
      release: newReleaseTitle.trim(),
      year: newReleaseYear.trim() || '',
      label: newReleaseLabels.join(', '),
      catNumbers: newReleaseCatNumbers.join(', '),
      labelsArray: newReleaseLabels,
      catNumbersArray: newReleaseCatNumbers,
      format: newReleaseFormat,
      mediaType: newReleaseType,
      earliestParentReleaseId: lookupReleaseId || base.earliestParentReleaseId,
      sourceReleaseId: releaseId.trim()
        ? [releaseId.trim()]
        : base.sourceReleaseId,
      isModified: true,
    };
  };

  const closeAndReset = () => {
    resetReleaseModalState();
    onClose();
  };

  const handleSubmit = async () => {
    if (newLabelInputOpen || newCatNoInputOpen) {
      setReleaseChangeSubmitError(
        `You have a ${
          newLabelInputOpen ? 'label' : 'catalogue number'
        } being entered. Please click Create to add it, or Cancel, before saving.`
      );
      return;
    }

    const dupNewLabel = findDuplicate(newReleaseLabels);
    if (dupNewLabel) {
      setReleaseChangeSubmitError(`Duplicate label: "${dupNewLabel}"`);
      return;
    }
    const dupNewCatNum = findDuplicate(newReleaseCatNumbers);
    if (dupNewCatNum) {
      setReleaseChangeSubmitError(
        `Duplicate catalogue number: "${dupNewCatNum}"`
      );
      return;
    }

    setReleaseChangeSubmitError(null);

    // Bulk "select" mode: don't perform the per-recording link — just return
    // the chosen release so the bulk apply can handle linking.
    if (mode === 'select') {
      onApplied(mergeReleaseIntoRow());
      resetReleaseModalState();
      return;
    }

    if (!userId || !row?.id) return;
    const isCreateAndLinkFlow =
      releaseSource === 'Other' || releaseLookupNotFound;
    setReleaseChangeSubmitLoading(true);
    try {
      if (isCreateAndLinkFlow) {
        const createRequest = {
          source: mapReleaseSourceToCreateEnum(releaseSource),
          releaseTitle: newReleaseTitle.trim(),
          dateReleased: newReleaseYear.trim() || undefined,
          releaseLabels:
            newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
          catNumbers:
            newReleaseCatNumbers.length > 0 ? newReleaseCatNumbers : undefined,
          format: newReleaseFormat || undefined,
          mediaType: newReleaseType || undefined,
          ...(releaseSource === 'Other'
            ? { sourceUrl: sourceUrl.trim() }
            : { sourceId: releaseId.trim() }),
        };
        await api.createAndLinkServiceResponse({
          userId,
          parentRecordingId: row.id,
          createReleaseAndLinkRequest: {
            ...createRequest,
            documentId: row.documentId,
          },
        });
      } else {
        if (!lookupReleaseId) {
          setReleaseChangeSubmitError('Missing release ID from lookup.');
          return;
        }
        await api.linkEarliestServiceResponse({
          userId,
          parentRecordingId: row.id,
          linkReleaseRequest: {
            releaseId: lookupReleaseId,
            edgeId: lookupEdgeId ?? undefined,
            dateReleased: lookupDateReleased ?? undefined,
            releaseTitle: newReleaseTitle.trim(),
            releaseLabels:
              newReleaseLabels.length > 0 ? newReleaseLabels : undefined,
            catNumbers:
              newReleaseCatNumbers.length > 0
                ? newReleaseCatNumbers
                : undefined,
            mediaType: newReleaseType || undefined,
            format: newReleaseFormat || undefined,
            documentId: row.documentId,
          },
        });
      }
      onApplied(mergeReleaseIntoRow());
      resetReleaseModalState();
      onClose();
    } catch (err) {
      setReleaseChangeSubmitError(await friendlySaveError(err, 'release-link'));
    } finally {
      setReleaseChangeSubmitLoading(false);
    }
  };

  const isCreateFlow = releaseSource === 'Other' || releaseLookupNotFound;

  return (
    <WModal
      isOpen={isOpen}
      onClose={closeAndReset}
      title={isCreateFlow ? 'Create and Link Release' : 'Change Linked Release'}
      modalClasses="tw-max-w-lg tw-w-full tw-mx-4 tw-max-h-[90vh]"
    >
      <div className="tw-mt-4 tw-overflow-y-auto tw-max-h-[calc(90vh-120px)]">
        <div className="tw-mb-4">
          <label
            htmlFor="release-source-input"
            className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
          >
            Release Source
          </label>
          <select
            id="release-source-input"
            value={releaseSource}
            onChange={(e) => {
              const newSource = e.target.value as ReleaseSourceForm;
              setReleaseSource(newSource);
              setReleaseId('');
              setSourceUrl('');
              setReleaseLookupError(null);
              setReleaseLookupSuccess(false);
              setReleaseLookupNotFound(false);
              setReleaseLookupNotLinked(false);
              setReleaseNotLinkedConfirmed(false);
              setReleaseSameAsCurrentError(false);
              if (newSource === 'Other' && !newReleaseTitle.trim()) {
                setNewReleaseTitle(current.release || '');
                setNewReleaseYear(current.year || '');
                setNewReleaseLabels([...currentLabels]);
                setNewReleaseCatNumbers([...currentCatNumbers]);
                setNewReleaseFormat(current.format || '');
                setNewReleaseType(current.mediaType || '');
              }
            }}
            className={`${SELECT_BASE_CLASS} ${selectTextClass(
              !!releaseSource
            )}`}
          >
            <option value="Discogs">Discogs</option>
            <option value="Deezer">Deezer</option>
            <option value="Spotify">Spotify</option>
            <option value="MusicBrainz">MusicBrainz</option>
            <option value="PPL">PPL</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {['Discogs', 'Deezer', 'Spotify', 'MusicBrainz', 'PPL'].includes(
          releaseSource
        ) ? (
          <div className="tw-mb-4">
            <label
              htmlFor="release-id-input"
              className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
            >
              Release ID
            </label>
            <div className="tw-flex tw-gap-2 tw-items-center">
              <input
                id="release-id-input"
                type="text"
                value={releaseId}
                onChange={(e) => {
                  setReleaseId(e.target.value);
                  setReleaseLookupError(null);
                  setReleaseLookupSuccess(false);
                  setReleaseLookupNotFound(false);
                  setReleaseLookupNotLinked(false);
                  setReleaseNotLinkedConfirmed(false);
                  setReleaseSameAsCurrentError(false);
                }}
                className="tw-flex-1 tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                placeholder="Enter release ID"
              />
              <WButton
                variant="secondary"
                label={releaseLookupLoading ? 'Looking up…' : 'Lookup'}
                disabled={
                  releaseLookupLoading ||
                  !releaseId.trim() ||
                  !userId ||
                  !row?.id
                }
                onClick={handleReleaseLookup}
              />
            </div>
            {releaseLookupError && (
              <p className="tw-text-westburyRed tw-text-xs tw-mt-2">
                {releaseLookupError}
              </p>
            )}
            {releaseLookupNotFound && (
              <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                <p className="tw-text-orange-400 tw-text-sm">
                  Release not found in WMC database, please provide details.
                </p>
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1 tw-min-w-0">
                    <label
                      htmlFor="change-release-notfound-title"
                      className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                    >
                      Release Title
                    </label>
                    <input
                      id="change-release-notfound-title"
                      type="text"
                      value={newReleaseTitle}
                      onChange={(e) => setNewReleaseTitle(e.target.value)}
                      className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                      placeholder="Enter release title"
                    />
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="change-release-notfound-year"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium"
                      >
                        Year of Release
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    <input
                      id="change-release-notfound-year"
                      type="text"
                      value={newReleaseYear}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setNewReleaseYear(v);
                        setNewReleaseYearError(validateReleaseYear(v));
                      }}
                      className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                        newReleaseYearError
                          ? 'tw-border-red-500'
                          : 'tw-border-gray-600'
                      }`}
                      placeholder="YYYY"
                      maxLength={4}
                    />
                    {newReleaseYearError && (
                      <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                        {newReleaseYearError}
                      </p>
                    )}
                  </div>
                </div>
                <MultiValueAddField
                  label="Label (Multiple)"
                  addLabel="Add label"
                  placeholder="Enter label"
                  inputId="change-release-notfound-labels-input"
                  values={newReleaseLabels}
                  onRemove={(index) =>
                    setNewReleaseLabels((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  open={newLabelInputOpen}
                  setOpen={setNewLabelInputOpen}
                  value={newLabelInputValue}
                  setValue={setNewLabelInputValue}
                  commit={commitNewLabel}
                />
                <MultiValueAddField
                  label="Catalogue Number (Multiple)"
                  addLabel="Add catalogue number"
                  placeholder="Enter catalogue number"
                  inputId="change-release-notfound-catno-input"
                  values={newReleaseCatNumbers}
                  onRemove={(index) =>
                    setNewReleaseCatNumbers((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  open={newCatNoInputOpen}
                  setOpen={setNewCatNoInputOpen}
                  value={newCatNoInputValue}
                  setValue={setNewCatNoInputValue}
                  commit={commitNewCatNo}
                />
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="change-release-notfound-format"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium"
                      >
                        Format
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    <select
                      id="change-release-notfound-format"
                      value={newReleaseFormat}
                      onChange={(e) => setNewReleaseFormat(e.target.value)}
                      className={`${SELECT_BASE_CLASS} ${selectTextClass(
                        !!newReleaseFormat
                      )}`}
                    >
                      <option value="">Select Format</option>
                      {availableFormats.map((format) => (
                        <option key={format} value={format}>
                          {format}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="tw-flex-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                      <label
                        htmlFor="change-release-notfound-type"
                        className="tw-block tw-text-white tw-text-sm tw-font-medium"
                      >
                        Type
                      </label>
                      <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                        Optional
                      </span>
                    </div>
                    <select
                      id="change-release-notfound-type"
                      value={newReleaseType}
                      onChange={(e) => setNewReleaseType(e.target.value)}
                      className={`${SELECT_BASE_CLASS} ${selectTextClass(
                        !!newReleaseType
                      )}`}
                    >
                      <option value="">Select Type</option>
                      {availableTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
            {releaseLookupSuccess && (
              <div className="tw-mt-4 tw-space-y-4 tw-pt-4 tw-border-t tw-border-gray-600">
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-flex-1">
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Release Title
                    </label>
                    <input
                      type="text"
                      value={newReleaseTitle}
                      readOnly
                      className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                    />
                  </div>
                  <div className="tw-w-full">
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Year of Release
                    </label>
                    <input
                      type="text"
                      value={newReleaseYear}
                      readOnly
                      className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                    />
                  </div>
                </div>
                <div>
                  <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                    Label (Multiple)
                  </label>
                  <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                    {newReleaseLabels.length > 0 ? (
                      newReleaseLabels.map((label, index) => (
                        <span
                          key={index}
                          className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                        >
                          {label}
                        </span>
                      ))
                    ) : (
                      <span className="tw-text-gray-500">—</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                    Catalogue Number (Multiple)
                  </label>
                  {newReleaseCatNumbers.length > 0 && (
                    <div className="tw-min-h-[40px] tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-flex tw-flex-wrap tw-gap-1.5 tw-items-center">
                      {newReleaseCatNumbers.length > 0 ? (
                        newReleaseCatNumbers.map((catNo, index) => (
                          <span
                            key={index}
                            className="tw-inline-flex tw-rounded-xs tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-bg-gray-700"
                          >
                            {catNo}
                          </span>
                        ))
                      ) : (
                        <span className="tw-text-gray-500">—</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
                  <div className="tw-w-full">
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Format
                    </label>
                    <input
                      type="text"
                      value={newReleaseFormat}
                      readOnly
                      className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                    />
                  </div>
                  <div className="tw-w-full">
                    <label className="tw-block tw-text-gray-400 tw-text-xs tw-font-medium tw-mb-1">
                      Type
                    </label>
                    <input
                      type="text"
                      value={newReleaseType}
                      readOnly
                      className="tw-w-full tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-cursor-not-allowed tw-opacity-90"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="tw-space-y-4">
            <div>
              <label
                htmlFor="new-release-source-url"
                className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
              >
                Source
              </label>
              <input
                id="new-release-source-url"
                type="text"
                value={sourceUrl}
                onChange={(e) => {
                  setSourceUrl(e.target.value);
                }}
                className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                placeholder="Provide release source details"
              />
            </div>
            <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <label
                  htmlFor="new-release-title"
                  className="tw-block tw-text-white tw-text-sm tw-font-medium tw-mb-2"
                >
                  Release Title
                </label>
                <input
                  id="new-release-title"
                  type="text"
                  value={newReleaseTitle}
                  onChange={(e) => setNewReleaseTitle(e.target.value)}
                  className="tw-w-full tw-bg-gray-700 tw-border tw-border-gray-600 tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800"
                  placeholder="Enter release title"
                />
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-year"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Year of Release
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                <input
                  id="new-release-year"
                  type="text"
                  value={newReleaseYear}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setNewReleaseYear(v);
                    setNewReleaseYearError(validateReleaseYear(v));
                  }}
                  className={`tw-w-full tw-bg-gray-700 tw-border tw-rounded tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-westburyBlue disabled:tw-cursor-not-allowed disabled:tw-opacity-60 disabled:tw-bg-gray-800 ${
                    newReleaseYearError
                      ? 'tw-border-red-500'
                      : 'tw-border-gray-600'
                  }`}
                  placeholder="YYYY"
                  maxLength={4}
                />
                {newReleaseYearError && (
                  <p className="tw-text-red-400 tw-text-xs tw-mt-1">
                    {newReleaseYearError}
                  </p>
                )}
              </div>
            </div>
            <MultiValueAddField
              label="Label (Multiple)"
              addLabel="Add label"
              placeholder="Enter label"
              inputId="new-release-labels-input"
              values={newReleaseLabels}
              onRemove={(index) =>
                setNewReleaseLabels((prev) =>
                  prev.filter((_, i) => i !== index)
                )
              }
              open={newLabelInputOpen}
              setOpen={setNewLabelInputOpen}
              value={newLabelInputValue}
              setValue={setNewLabelInputValue}
              commit={commitNewLabel}
            />
            <MultiValueAddField
              label="Catalogue Number (Multiple)"
              addLabel="Add catalogue number"
              placeholder="Enter catalogue number"
              inputId="new-release-catno-input"
              values={newReleaseCatNumbers}
              onRemove={(index) =>
                setNewReleaseCatNumbers((prev) =>
                  prev.filter((_, i) => i !== index)
                )
              }
              open={newCatNoInputOpen}
              setOpen={setNewCatNoInputOpen}
              value={newCatNoInputValue}
              setValue={setNewCatNoInputValue}
              commit={commitNewCatNo}
            />
            <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-format"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Format
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                <select
                  id="new-release-format"
                  value={newReleaseFormat}
                  onChange={(e) => setNewReleaseFormat(e.target.value)}
                  className={`${SELECT_BASE_CLASS} ${selectTextClass(
                    !!newReleaseFormat
                  )}`}
                >
                  <option value="">Select Format</option>
                  {availableFormats.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <label
                    htmlFor="new-release-type"
                    className="tw-block tw-text-white tw-text-sm tw-font-medium"
                  >
                    Type
                  </label>
                  <span className="tw-text-xs tw-leading-4 tw-text-gray-400">
                    Optional
                  </span>
                </div>
                <select
                  id="new-release-type"
                  value={newReleaseType}
                  onChange={(e) => setNewReleaseType(e.target.value)}
                  className={`${SELECT_BASE_CLASS} ${selectTextClass(
                    !!newReleaseType
                  )}`}
                >
                  <option value="">Select Type</option>
                  {availableTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        {releaseLookupNotLinked && (
          <div
            className="tw-rounded-lg tw-border tw-border-orange-500/60 tw-bg-orange-500/10 tw-p-4"
            role="alert"
          >
            <p className="tw-text-orange-400 tw-text-sm tw-font-medium tw-mb-2">
              Confirm Release Linking
            </p>
            <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
              This release is already linked to a different recording.
            </p>
            <p className="tw-text-gray-300 tw-text-sm tw-mb-4">
              Please double-check that you are linking it to the correct
              recording and not a different version.
            </p>
            <label
              htmlFor="change-release-not-linked-confirmed"
              className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
            >
              <input
                id="change-release-not-linked-confirmed"
                type="checkbox"
                checked={releaseNotLinkedConfirmed}
                onChange={(e) => setReleaseNotLinkedConfirmed(e.target.checked)}
                className="tw-mt-0.5 tw-rounded tw-border-gray-600 tw-bg-gray-700 tw-text-orange-500 focus:tw-ring-orange-500"
              />
              <span className="tw-text-sm tw-text-white">
                I have read and understood this warning
              </span>
            </label>
          </div>
        )}
        {releaseSameAsCurrentError && (
          <p className="tw-text-westburyRed tw-text-xs tw-mt-2 tw-mb-2">
            This recording is already linked to this release
          </p>
        )}
        {releaseChangeSubmitError && (
          <p className="tw-text-westburyRed tw-text-xs tw-mt-2 tw-mb-2">
            {releaseChangeSubmitError}
          </p>
        )}
        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-600">
          <WButton variant="secondary" label="Cancel" onClick={closeAndReset} />
          <WButton
            variant="primary"
            label={
              releaseChangeSubmitLoading
                ? 'Saving…'
                : mode === 'select'
                ? 'Select release'
                : isCreateFlow
                ? 'Create and Link Release'
                : 'Change Linked Release'
            }
            disabled={
              releaseChangeSubmitLoading ||
              releaseSameAsCurrentError ||
              !!newReleaseYearError ||
              (isCreateFlow
                ? !newReleaseTitle.trim() ||
                  (releaseSource === 'Other' && !sourceUrl.trim())
                : !releaseLookupSuccess ||
                  !lookupReleaseId ||
                  (releaseLookupNotLinked && !releaseNotLinkedConfirmed))
            }
            onClick={handleSubmit}
          />
        </div>
      </div>
    </WModal>
  );
};

export default ChangeReleaseModal;
